/**
 * Link Visual Attributes — color/dash mapping, filtering, validation,
 * and accessibility helpers for card link visualization.
 *
 * Requirements: 4.1, 4.2, 4.3, 4.6, 3.2, 2.5, 8.2
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LinkVisualAttributes {
  readonly color: string;
  readonly dashArray: string;
  readonly label: string;
}

export type VisualizableLinkType = 'related' | 'blocks' | 'blocked-by';

export interface VisibleLink {
  readonly linkId: string;
  readonly targetCardId: string;
  readonly linkType: VisualizableLinkType;
}

export interface ApiLinkItem {
  readonly linkId: string;
  readonly linkType: string;
  readonly targetCardId: string;
  readonly targetCardTitle: string;
  readonly targetCardStatus: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const VISUALIZABLE_LINK_TYPES: ReadonlySet<string> = new Set([
  'related',
  'blocks',
  'blocked-by',
]);

const MAX_VISIBLE_LINKS = 50;

export const LINK_VISUAL_MAP: Record<VisualizableLinkType, LinkVisualAttributes> = {
  related: { color: '#60A5FA', dashArray: '0', label: 'Related' },       // bright blue (neon in dark)
  blocks: { color: '#F87171', dashArray: '8,4', label: 'Blocks' },       // bright red (neon in dark)
  'blocked-by': { color: '#FB923C', dashArray: '3,3', label: 'Blocked By' }, // bright orange (neon in dark)
};

export const DEFAULT_VISUAL: LinkVisualAttributes = {
  color: '#6B7280',
  dashArray: '0',
  label: 'Unknown',
};

// ---------------------------------------------------------------------------
// Public Functions
// ---------------------------------------------------------------------------

/**
 * Returns the visual attributes (color, dash pattern, label) for a link type.
 * Known types get their defined attributes; unknown types get gray/solid.
 */
export function getLinkVisualAttributes(linkType: string): LinkVisualAttributes {
  if (isVisualizableLinkType(linkType)) {
    return LINK_VISUAL_MAP[linkType];
  }
  return DEFAULT_VISUAL;
}

/**
 * Filters raw API link items to only those with visualizable link types
 * whose target cards are currently visible on the board, capped at 50.
 */
export function filterVisibleLinks(
  apiLinks: readonly ApiLinkItem[],
  visibleCardIds: ReadonlySet<string>
): VisibleLink[] {
  const result: VisibleLink[] = [];

  for (const link of apiLinks) {
    if (result.length >= MAX_VISIBLE_LINKS) break;

    if (
      isVisualizableLinkType(link.linkType) &&
      visibleCardIds.has(link.targetCardId)
    ) {
      result.push({
        linkId: link.linkId,
        targetCardId: link.targetCardId,
        linkType: link.linkType,
      });
    }
  }

  return result;
}

/**
 * Validates that a timeout value is within the acceptable range [1, 10].
 * Returns true if the value is a whole number between 1 and 10 inclusive.
 */
export function validateTimeout(value: number): boolean {
  return Number.isInteger(value) && value >= 1 && value <= 10;
}

/**
 * Generates an accessibility description string for a set of visible links.
 * Format: "N linked cards: X blocks, Y related, Z blocked-by"
 */
export function generateAriaDescription(links: readonly VisibleLink[]): string {
  if (links.length === 0) return '';

  const counts: Record<VisualizableLinkType, number> = {
    related: 0,
    blocks: 0,
    'blocked-by': 0,
  };

  for (const link of links) {
    counts[link.linkType]++;
  }

  const parts: string[] = [];
  if (counts.blocks > 0) parts.push(`${counts.blocks} blocks`);
  if (counts.related > 0) parts.push(`${counts.related} related`);
  if (counts['blocked-by'] > 0) parts.push(`${counts['blocked-by']} blocked-by`);

  return `${links.length} linked cards: ${parts.join(', ')}`;
}

// ---------------------------------------------------------------------------
// Internal Helpers
// ---------------------------------------------------------------------------

function isVisualizableLinkType(linkType: string): linkType is VisualizableLinkType {
  return VISUALIZABLE_LINK_TYPES.has(linkType);
}
